import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Popover,
  styled,
  tableCellClasses,
  Tooltip,
  Chip,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { isMobile } from "../../globalStyles/mobile";
import Pagination from "../pagination";
import FilterModal from "../filterModal";
import { useTheme } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import TableHeader from "../table-header";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { Send } from "@mui/icons-material";
import { notificationService } from "../../utils/services/notificationService";
import { toast } from "react-toastify";
import { formatDateTime } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const formatDelayTime = (delayTime) => {
  if (!delayTime) return delayTime;

  const hours = parseInt(delayTime, 10);

  if (hours > 48) {
    const days = Math.round(hours / 24);
    return `${days} day${days > 1 ? "s" : ""}`;
  }

  return delayTime + " hours";
};

const getTypeColor = (type) => {
  switch (type) {
    case "shipment":
      return { backgroundColor: "#E3F2FD", color: "#1976D2" };
    case "Order Process":
      return { backgroundColor: "#E8F5E8", color: "#2E7D32" };
    case "Order Fulfillment":
      return { backgroundColor: "#FFF3E0", color: "#F57C00" };
    default:
      return { backgroundColor: "#F5F5F5", color: "#616161" };
  }
};

const NotificationList = ({
  data,
  setData,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  onChangeFilter,
  filterData,
  selectedItems,
  setSelectedItems,
  setNotifyLoader,
  notifyLoader,
  fetchNotifications,
  client_id,
}) => {
  const theme = useTheme();
  const defaultColor = "#D13438";
  const navigate = useNavigate();
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
    type: "",
  });
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const clientId = localStorage.getItem("client_id");
  const startIndex = page * rowsPerPage;
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [exposeModalOpen, setExposeModalOpen] = useState(false);
  const [selectedNotificationForExpose, setSelectedNotificationForExpose] =
    useState(null);
  const [editedMessage, setEditedMessage] = useState("");
  const [trailModalOpen, setTrailModalOpen] = useState(false);
  const [selectedTrail, setSelectedTrail] = useState([]);
  const openPopover = Boolean(anchorElPopover);

  // Determine the selected type based on selectedItems
  const selectedType = selectedItems.length > 0 ? selectedItems[0].type : null;

  const handleCheckboxChange = (item) => {
    setSelectedItems((prev) => {
      const isSelected = prev.some((selected) => selected._id === item._id);
      if (isSelected) {
        // Deselecting an item
        const newSelected = prev.filter(
          (selected) => selected._id !== item._id,
        );
        return newSelected;
      } else {
        // Selecting a new item
        if (prev.length === 0) {
          // If no items are selected, allow selecting this item
          return [item];
        } else if (item.type === prev[0].type) {
          // Only allow selecting if the type matches the already selected items
          return [...prev, item];
        }
        // If type doesn't match, do nothing
        return prev;
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      if (selectedItems.length === 0) {
        // If no items are selected, select all items on the current page
        setSelectedItems(data);
      } else {
        // If items are already selected, only select items of the same type
        setSelectedItems(data.filter((item) => item.type === selectedType));
      }
    } else {
      setSelectedItems([]);
    }
  };

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key == filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handlePopoverClose = () => {
    setAnchorElPopover(null);
  };

  const handlePopoverOpen = (event, item) => {
    console.log(item);
    setSelectedNotification(item);
    event.stopPropagation();
    setAnchorElPopover(event.currentTarget);
  };

  const handleNotifyOps = async () => {
    try {
      setNotifyLoader(true);
      let notification = await notificationService.notifyOps(
        selectedNotification._id,
        clientId,
      );
      toast.success("Notification sent to OPS successfully", {
        autoClose: 3000,
      });
      setData((prev) =>
        prev.map((x) => (x._id === notification._id ? notification : x)),
      );
      handlePopoverClose();
    } catch (error) {
      console.error(error);
    } finally {
      setNotifyLoader(false);
    }
  };

  const handleExposeToClient = (notification) => {
    setSelectedNotificationForExpose(notification);
    setEditedMessage(notification.message || "");
    setExposeModalOpen(true);
    handlePopoverClose();
  };

  const handleExposeModalClose = () => {
    setExposeModalOpen(false);
    setSelectedNotificationForExpose(null);
    setEditedMessage("");
    fetchNotifications();
  };

  const handleExposeSubmit = async () => {
    try {
      setNotifyLoader(true);
      await notificationService.updateNotification(
        selectedNotificationForExpose._id,
        {
          message: editedMessage,
          client_id: selectedNotificationForExpose.data?.client,
        },
      );
      toast.success("Notification exposed to client successfully", {
        autoClose: 3000,
      });
      handleExposeModalClose();
      // You might want to refresh the data here
    } catch (error) {
      console.error(error);
      toast.error("Failed to expose notification to client", {
        autoClose: 3000,
      });
    } finally {
      setNotifyLoader(false);
    }
  };

  useEffect(() => {
    setNotifications(
      data.map((item) => ({
        ...item,
        action: (
          <Box sx={{ display: "flex", boxShadow: "none" }}>
            <IconButton
              aria-label="more actions"
              onClick={(e) => handlePopoverOpen(e, item)}
              sx={{
                color: openPopover ? "primary" : "inherit",
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Popover
              open={Boolean(anchorElPopover)}
              anchorEl={anchorElPopover}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: isMobile() ? "bottom" : "top",
                horizontal: isMobile() ? "center" : "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": {
                  boxShadow: "none",
                  border: "1px solid var(--border-color)",
                  borderRadius: "4px",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "10px",
                  boxShadow: "none",
                  gap: 2,
                }}
              >
                <Button
                  style={{
                    color: "var(--text-tertiary)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  onClick={() => handleNotifyOps()}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <Send fill={primary_color} />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"var(--text-tertiary)"}
                      fontWeight={500}
                    >
                      Notify OPS
                    </Typography>
                  </Box>
                </Button>
                {selectedNotification?.notify_details?.length ? (
                  <Button
                    style={{
                      color: "var(--text-tertiary)",
                      justifyContent: "flex-start",
                      textTransform: "none",
                    }}
                    onClick={() => handleViewTrail()}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      width="100%"
                      gap={1}
                    >
                      <ViewIcon fill={primary_color} />
                      <Typography
                        fontFamily={"poppins"}
                        fontSize={"16px"}
                        color={"var(--text-tertiary)"}
                        fontWeight={500}
                      >
                        Email Send History (
                        {selectedNotification?.notify_details?.length || 0})
                      </Typography>
                    </Box>
                  </Button>
                ) : (
                  <></>
                )}
                {selectedNotification?.type === "Order Fulfillment" &&
                  selectedNotification?.data?.type === "Manifested Delay" &&
                  !selectedNotification.client_id && (
                    <Button
                      style={{
                        color: "var(--text-tertiary)",
                        justifyContent: "flex-start",
                        textTransform: "none",
                      }}
                      onClick={() => handleExposeToClient(selectedNotification)}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        width="100%"
                        gap={1}
                      >
                        <ViewIcon fill={primary_color} />
                        <Typography
                          fontFamily={"poppins"}
                          fontSize={"16px"}
                          color={"var(--text-tertiary)"}
                          fontWeight={500}
                        >
                          Expose to client
                        </Typography>
                      </Box>
                    </Button>
                  )}
              </Box>
            </Popover>
          </Box>
        ),
      })),
    );
  }, [data, anchorElPopover]);

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", minHeight: "100%" }}
      />
    </div>
  );

  const renderEventDetails = (x) => {
    return x?.type === "shipment" ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"Order#:"}
          </Typography>
          <Tooltip title="Order Flow">
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() =>
                navigate(`/order-flow/${encodeURIComponent(x?.data?.order_id)}`)
              }
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.order_id}
            </Typography>
          </Tooltip>
        </div>
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {`Shipment : ${x?.data?.event?.id}`}
        </Typography>
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {`Tracking Number : ${x?.data?.event?.tracking_number}`}
        </Typography>
      </div>
    ) : ["Order Process", "Order Fulfillment"].includes(x?.type) ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"Order#:"}
          </Typography>
          <Tooltip
            title={
              x?.type === "Order Process" ? "Order Flow" : "Order Tracking"
            }
          >
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() =>
                x.type === "Order Process"
                  ? navigate(
                      `/order-flow/${encodeURIComponent(x?.data?.order_id)}`,
                    )
                  : navigate(
                      `/order-tracking-history/${encodeURIComponent(
                        x?.data?.order,
                      )}`,
                    )
              }
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.order_id}
            </Typography>
          </Tooltip>
        </div>
        {x.data.type === "Manifested Delay" ? (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Typography
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {"Delay Time#:"}
            </Typography>
            <Typography
              sx={{
                marginLeft: 1,
              }}
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {formatDelayTime(Math.round(x?.data?.delay_time || 0))}
            </Typography>
          </div>
        ) : null}
      </div>
    ) : x.type === "Inbound Delay" ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"PO#:"}
          </Typography>
          <Tooltip title={"ASN Tracking"}>
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() =>
                navigate(`/asn-tracking/${encodeURIComponent(x?.data?.po)}`)
              }
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.asn_no}
            </Typography>
          </Tooltip>
        </div>
      </div>
    ) : x.type === "Purchase Order" ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"SKU#:"}
          </Typography>
          <Tooltip title={"Purchase Order"}>
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() => navigate(`/purchase-order`)}
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.sku}
            </Typography>
          </Tooltip>
        </div>
      </div>
    ) : x.type === "Supplier Config" ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"Item#:"}
          </Typography>
          <Tooltip title={"Supplier Config"}>
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() =>
                navigate("/inventory-config", {
                  state: { product: { _id: x?.data?.product } },
                })
              }
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.productName}
            </Typography>
          </Tooltip>
        </div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"SKU: "}
          </Typography>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
            sx={{ marginLeft: 1 }}
          >
            {x?.data?.sku}
          </Typography>
        </div>
      </div>
    ) : x.type === "Data Error" ? (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"PO#:"}
          </Typography>
          <Tooltip title={"Purchase Order"}>
            <Typography
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                marginLeft: 1,
              }}
              onClick={() => {
                localStorage.setItem(
                  "po-filter-data",
                  JSON.stringify({
                    filters: [
                      {
                        key: "asn_no",
                        values: [x?.data?.purchase_order],
                      },
                    ],
                  }),
                );
                navigate(`/purchase-order?child=true`);
              }}
              fontSize="12px"
              color="var(--text-secondary)"
              fontWeight={400}
              fontFamily={"Poppins"}
            >
              {x?.data?.purchase_order}
            </Typography>
          </Tooltip>
        </div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
          >
            {"Sender: "}
          </Typography>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily={"Poppins"}
            sx={{ marginLeft: 1 }}
          >
            {x?.data?.sender}
          </Typography>
        </div>
      </div>
    ) : (
      <></>
    );
  };

  const TableSchema = [
    // {
    //   key: "",
    //   title: "",
    //   render: (item) => (
    //     <Checkbox
    //       checked={selectedItems.some((x) => item._id === x._id)}
    //       onChange={() => handleCheckboxChange(item)}
    //       disabled={selectedType && item.type !== selectedType}
    //     />
    //   ),
    // },
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
          ml={1}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "type",
      title: "Type",
      handleOpenFilter: handleOpenFilter,
      render: (item) => (
        <Chip
          label={item?.type}
          size="small"
          sx={{
            fontSize: "11px",
            fontWeight: 500,
            fontFamily: "Poppins",
            ...getTypeColor(item?.type),
            borderRadius: "16px",
            textTransform: "capitalize",
          }}
        />
      ),
    },
    ...(client_id
      ? []
      : [
          {
            key: "client_id",
            title: "Client",
            render: (item) => (
              <Typography
                fontSize="12px"
                color="var(--text-secondary)"
                fontWeight={400}
                fontFamily={"Poppins"}
              >
                {item?.client_id?.business_name || "N/A"}
              </Typography>
            ),
            handleOpenFilter: handleOpenFilter,
          },
        ]),
    // {
    //   key: "status",
    //   title: "Status",
    //   displayKey: "status",
    // },
    {
      key: "details",
      title: "Details",
      render: (item) => renderEventDetails(item),
    },
    {
      key: "message",
      title: "Message",
      displayKey: "message",
    },
    {
      key: "createdAt",
      title: "Triggered At",
      render: (item) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {formatDateTime(item?.createdAt)}
        </Typography>
      ),
      handleOpenFilter: handleOpenFilter,
    },
    {
      key: "actions",
      title: "Actions",
      displayKey: "action",
    },
  ];

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      <TableContainer
        sx={{
          overflowY: "auto",
          paddingInline: 0.4,
          maxHeight: "100%",
          // borderRadius: "8px",
          // boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            // border: "1px solid #E2E8F0",
            // borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((x) => (
                <TableHeader
                  filterData={filterData}
                  filterKey={x.key}
                  handleOpenFilter={x.handleOpenFilter}
                  title={x.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {notifications.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={7} align="center">
                  <NoDataImage />
                </TableCell>
              </StyledTableRow>
            ) : (
              notifications?.map((x, index) => (
                <StyledTableRow key={index}>
                  {TableSchema.map((column) => (
                    <StyledTableCell
                      key={column.key}
                      sx={{ width: column.width }}
                    >
                      {column.displayKey ? (
                        <Typography
                          fontSize="12px"
                          color="var(--text-secondary)"
                          fontWeight={400}
                          fontFamily={"Poppins"}
                        >
                          {x[column.displayKey] || "--"}
                        </Typography>
                      ) : column.render ? (
                        column.render(x, index)
                      ) : null}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  const handleViewTrail = () => {
    setSelectedTrail(selectedNotification.notify_details || []);
    setTrailModalOpen(true);
    handlePopoverClose();
  };

  return (
    <>
      <div sx={{ minWidth: "100%" }}>
        <div
          style={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : `calc(100vh - 250px)`,
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {renderListView()}
        </div>

        <Pagination
          dataCount={dataCount}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          disableNext={dataCount <= (page + 1) * filterData.limit}
        />
        <Popover
          open={Boolean(anchorElFilter)}
          anchorEl={anchorElFilter}
          onClose={handleCloseFilter}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableScrollLock={true}
          sx={{ zIndex: 1500 }}
        >
          <FilterModal
            open={openFilterModal}
            onClose={handleCloseFilter}
            options={selectedfilterData?.options}
            selectedValues={selectedfilterData?.filter}
            onSave={handleSaveFilter}
            currentSortOrder={selectedfilterData?.sort}
            filterKey={selectedfilterData?.key}
            hasMore={selectedfilterData?.loadMoreButton}
            displayKey={selectedfilterData?.displayKey}
            id={selectedfilterData?.id}
            fetchApi={selectedfilterData?.fetchApi}
            searchKey={selectedfilterData?.searchKey}
            type={selectedfilterData?.type}
          />
        </Popover>

        <Dialog
          open={exposeModalOpen}
          onClose={handleExposeModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography
              fontFamily={"poppins"}
              fontSize={"20px"}
              color={"var(--text-tertiary)"}
              fontWeight={600}
            >
              Expose Notification to Client
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography
                fontSize="14px"
                color="var(--text-secondary)"
                fontWeight={500}
                fontFamily={"Poppins"}
                sx={{ mb: 1 }}
              >
                Type: {selectedNotificationForExpose?.type}
              </Typography>
              <Typography
                fontSize="14px"
                color="var(--text-secondary)"
                fontWeight={500}
                fontFamily={"Poppins"}
                sx={{ mb: 1 }}
              >
                Status: {selectedNotificationForExpose?.status}
              </Typography>
              <Typography
                fontSize="14px"
                color="var(--text-secondary)"
                fontWeight={500}
                fontFamily={"Poppins"}
                sx={{ mb: 2 }}
              >
                Triggered At:{" "}
                {selectedNotificationForExpose?.createdAt
                  ? formatDateTime(selectedNotificationForExpose.createdAt)
                  : ""}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                fontSize="14px"
                color="var(--text-secondary)"
                fontWeight={500}
                fontFamily={"Poppins"}
                sx={{ mb: 1 }}
              >
                Details:
              </Typography>
              <Box sx={{ pl: 2 }}>
                {selectedNotificationForExpose &&
                  renderEventDetails(selectedNotificationForExpose)}
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleExposeModalClose} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleExposeSubmit}
              variant="contained"
              color="primary"
              disabled={notifyLoader}
            >
              {notifyLoader ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={trailModalOpen}
          onClose={() => setTrailModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ backgroundColor: primary_color, color: "white" }}>
            <Typography
              fontFamily={"poppins"}
              fontSize={"20px"}
              color={"white"}
              fontWeight={600}
            >
              Email Send History
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedTrail.length === 0 ? (
              <Typography>No notifications sent yet.</Typography>
            ) : (
              <Box>
                {selectedTrail.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid var(--border-color)",
                      borderRadius: 1,
                      backgroundColor:
                        index % 2 === 0
                          ? "var(--row-bg-even)"
                          : "var(--row-bg-odd)",
                    }}
                  >
                    <Typography fontSize="14px" fontWeight={500} sx={{ mb: 1 }}>
                      {index + 1}. {detail.subject}
                    </Typography>
                    <Typography
                      fontSize="12px"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Sent At:{" "}
                      {detail.sent_at
                        ? formatDateTime(detail.sent_at)
                        : "N/A"}
                    </Typography>
                    <Typography fontSize="14px" sx={{ mb: 1 }}>
                      Recipients: {detail.email_ids?.join(", ")}
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        border: "1px solid var(--border-color)",
                        borderRadius: 1,
                        backgroundColor: "var(--row-bg-even)",
                        maxHeight: 200,
                        overflowY: "auto",
                      }}
                      dangerouslySetInnerHTML={{ __html: detail.content }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTrailModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default NotificationList;
